import { FunctionComponent, ReactNode } from 'react';
import { Box, FormControl, MenuItem, Stack, Typography } from '@mui/material';
import { TextField } from '@nps.today/component-library';
import { Claim, TenantCapabilities } from '../../../@types/organization';
import {
  getClaimsByTemplateRole,
  USER_ACCESS_TEMPLATE_ROLES
} from '../../../utils/userAccessTemplates';
import { useTranslator } from 'translation/useTranslator';
import OrganizationUserPermissions from './OrganizationUserPermissions';
import { CUSTOM_TEMPLATE_VALUE, permissionsMatchTemplateRole } from './permissions.helpers';

type UserAccessFieldsProps = {
  role: string;
  claims: Claim[];
  capabilities: TenantCapabilities;
  assignableRoles: string[];
  canAssignClaims: boolean;
  showPermissions: boolean;
  onRoleChange: (role: string, claims: Claim[]) => void;
  onClaimsChange: (claims: Claim[]) => void;
  dataTestId: string;
  disabled?: boolean;
  error?: string;
  initialClaims?: Claim[];
  userField?: ReactNode;
};

const UserAccessFields: FunctionComponent<UserAccessFieldsProps> = ({
  role,
  claims,
  capabilities,
  assignableRoles,
  canAssignClaims,
  showPermissions,
  onRoleChange,
  onClaimsChange,
  dataTestId,
  disabled,
  error,
  initialClaims,
  userField
}) => {
  const translator = useTranslator();
  const isTemplateCustomized = !permissionsMatchTemplateRole(claims, role, capabilities);

  const handleTemplateChange = (templateRole: string) => {
    if (templateRole === CUSTOM_TEMPLATE_VALUE) {
      return;
    }
    onRoleChange(templateRole, getClaimsByTemplateRole(templateRole, capabilities));
  };

  return (
    <Stack spacing={2}>
      <FormControl sx={{ width: '100%' }}>
        <TextField
          dataTestId={`${dataTestId}-role`}
          disabled={disabled}
          select
          label={translator.userAccessTemplate()}
          fullWidth
          value={isTemplateCustomized ? CUSTOM_TEMPLATE_VALUE : role}
          onChange={(event: any) => handleTemplateChange(event.target.value)}
          error={Boolean(error)}
          helperText={error}
        >
          {isTemplateCustomized && (
            <MenuItem value={CUSTOM_TEMPLATE_VALUE} disabled>
              {translator.custom()}
            </MenuItem>
          )}
          {assignableRoles.includes('CompanyAdmin') && (
            <MenuItem value={USER_ACCESS_TEMPLATE_ROLES.admin}>
              {translator.companyAdmin()}
            </MenuItem>
          )}
          {assignableRoles.includes('CompanyUser') && (
            <MenuItem value={USER_ACCESS_TEMPLATE_ROLES.campaignManager}>
              {translator.campaignManager()}
            </MenuItem>
          )}
        </TextField>
      </FormControl>

      {userField}

      {showPermissions && (
        <Box
          sx={{
            maxHeight: '55vh',
            overflowY: 'auto',
            borderTop: '1px solid',
            borderColor: 'divider',
            pt: 2
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            {translator.permissions()}
          </Typography>
          <OrganizationUserPermissions
            selectedClaims={claims}
            isEditable
            canAssignClaims={canAssignClaims}
            capabilities={capabilities}
            onClaimsChange={onClaimsChange}
            initialClaims={initialClaims}
          />
        </Box>
      )}
    </Stack>
  );
};

export default UserAccessFields;
